/**
 * Training — /training
 * ════════════════════
 * Lists all published training articles with category filter.
 * Content from ContentContext (localStorage + seed data).
 */
import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useContent } from '../context/ContentContext';
import { useSEO } from '../utils/seo';
import Navbar from '../components/Navbar';
import './Training.css';

function ArticleCard({ article, CATEGORIES }) {
  const cat = CATEGORIES.find(c => c.id === article.category) || CATEGORIES[0];
  return (
    <Link to={`/training/${article.slug}`} className="ta-card">
      {/* Emoji cover */}
      <div className="ta-card-cover" style={{ background: `${cat.color}15` }}>
        <span className="ta-card-emoji">{article.emoji || '📄'}</span>
        {article.featured && <span className="ta-card-featured">★ Featured</span>}
      </div>

      <div className="ta-card-body">
        {/* Category badge */}
        <span className="ta-card-cat" style={{ color: cat.color, background: `${cat.color}15` }}>
          {cat.icon} {cat.label}
        </span>

        <h3 className="ta-card-title">{article.title}</h3>
        <p className="ta-card-excerpt">{article.excerpt}</p>

        <div className="ta-card-meta">
          <span>⏱ {article.readTimeMins} min read</span>
          <span>{new Date(article.publishedAt).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}</span>
        </div>
      </div>
    </Link>
  );
}

export default function Training({ onSignIn }) {
  useSEO({
    title:       'Mentor Training — Guides & Best Practices',
    description: 'Learn how to get the most from your mentoring sessions. Tips, guides and best practices to help you grow as a developer.',
    canonical:   '/training',
  });
  const { getPublishedArticles, getFeaturedArticles, CATEGORIES } = useContent();
  const [activeCat, setActiveCat] = useState('all');
  const [search,    setSearch]    = useState('');

  const allArticles = getPublishedArticles();
  const featured    = getFeaturedArticles();

  const filtered = useMemo(() => {
    let list = allArticles;
    if (activeCat !== 'all') list = list.filter(a => a.category === activeCat);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(a =>
        a.title.toLowerCase().includes(q) ||
        a.excerpt.toLowerCase().includes(q)
      );
    }
    return list;
  }, [allArticles, activeCat, search]);

  // Category counts
  const counts = useMemo(() => {
    const map = {};
    allArticles.forEach(a => { map[a.category] = (map[a.category] || 0) + 1; });
    return map;
  }, [allArticles]);

  return (
    <>
<Navbar onSignIn={onSignIn} />

      <div className="ta-page">

        {/* ── Hero ──────────────────────────────────────── */}
        <div className="ta-hero">
          <div className="ta-hero-inner">
            <div className="ta-hero-badge">📚 Mentor Training Hub</div>
            <h1 className="ta-hero-title">Learn. Prepare. Grow.</h1>
            <p className="ta-hero-sub">
              Guides written by the DevBrains team to help you get the maximum
              value from every session.
            </p>

            {/* Search */}
            <div className="ta-search-wrap">
              <span className="ta-search-icon">🔍</span>
              <input
                className="ta-search"
                placeholder="Search guides…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <button className="ta-search-clear" onClick={() => setSearch('')}>✕</button>
              )}
            </div>

            {/* Stats row */}
            <div className="ta-hero-stats">
              {[
                { n: allArticles.length, l: 'Guides' },
                { n: CATEGORIES.length,  l: 'Topics' },
                { n: featured.length,    l: 'Featured' },
              ].map(s => (
                <div key={s.l} className="ta-hero-stat">
                  <strong>{s.n}</strong><span>{s.l}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="ta-content">

          {/* ── Featured strip (if no filter/search active) ── */}
          {activeCat === 'all' && !search && featured.length > 0 && (
            <section className="ta-featured-sec">
              <div className="ta-section-label">★ Featured Guides</div>
              <div className="ta-featured-grid">
                {featured.map(a => (
                  <Link key={a.id} to={`/training/${a.slug}`} className="ta-featured-card">
                    <span className="ta-featured-emoji">{a.emoji || '📄'}</span>
                    <div>
                      <div className="ta-featured-title">{a.title}</div>
                      <div className="ta-featured-meta">⏱ {a.readTimeMins} min read</div>
                    </div>
                    <span className="ta-featured-arrow">→</span>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* ── Category filter pills ──────────────────────── */}
          <div className="ta-cats">
            <button
              className={`ta-cat-pill ${activeCat === 'all' ? 'active' : ''}`}
              onClick={() => setActiveCat('all')}
            >
              All <span className="ta-cat-count">{allArticles.length}</span>
            </button>
            {CATEGORIES.filter(c => counts[c.id]).map(c => (
              <button
                key={c.id}
                className={`ta-cat-pill ${activeCat === c.id ? 'active' : ''}`}
                onClick={() => setActiveCat(c.id)}
                style={activeCat === c.id ? { borderColor: c.color, color: c.color, background: `${c.color}12` } : {}}
              >
                {c.icon} {c.label} <span className="ta-cat-count">{counts[c.id]}</span>
              </button>
            ))}
          </div>

          {/* ── Results ───────────────────────────────────── */}
          {filtered.length === 0 ? (
            <div className="ta-empty">
              <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>No guides found</div>
              <div style={{ color: '#9ca3af', fontSize: '.84rem' }}>
                Try a different search term or category
              </div>
            </div>
          ) : (
            <>
              <div className="ta-results-label">
                {search || activeCat !== 'all'
                  ? `${filtered.length} result${filtered.length !== 1 ? 's' : ''}`
                  : `All ${filtered.length} guides`}
              </div>
              <div className="ta-grid">
                {filtered.map(a => (
                  <ArticleCard key={a.id} article={a} CATEGORIES={CATEGORIES} />
                ))}
              </div>
            </>
          )}

          {/* ── Bottom CTA ────────────────────────────────── */}
          <div className="ta-bottom-cta">
            <div className="ta-bottom-cta-inner">
              <div className="ta-bottom-cta-emoji">🚀</div>
              <h3>Ready to apply what you've learned?</h3>
              <p>Book a 1:1 session with a verified mentor and put these strategies into practice.</p>
              <Link to="/mentors" className="ta-cta-btn">Find Your Mentor →</Link>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
